import * as React from 'react';
import { Input, FormPanel, SubmitButton, Button, Select, InputError } from '@ixicare/ui';
import connect from '~/store/connect';
import { useNavigate } from 'react-router-dom';
import { getDefaultReducerChange, getDefaultStateReducer } from '~/utils/reducer';
import SiteStore from '../store/Site';
import { SiteBody, SiteEntryProps } from '@ixicare/resources';
import { State } from '~@types/store';
import { DeleteSiteConfirmationModal, ResetDemokitConfirmationModal } from '~containers/Site';
import { t } from '~i18n';
import { flattenOrganizations, useOrganizations } from '../hooks/useOrganizations';
import { useStores } from '~/store/utils';

interface SiteSettingsFormContainerProps {
  createBuilding: (body: SiteBody) => Promise<SiteEntryProps>;
  archiveBuilding: (siteUuid: string) => Promise<SiteEntryProps>;
  resetDemokit: (siteId: number) => Promise<any>;
  site?: SiteStore;
}
interface SiteSettingsFormContainerState {
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  lat: number;
  long: number;
  alertContactName: string;
  alertContactPhoneNumber: string;
  alertContactEmail: string;
  doorCode: string;
  vatNumber: string;
  organizationId: string | null;
}
const SiteSettingsFormContainer: React.FC<SiteSettingsFormContainerProps> = ({
  createBuilding,
  archiveBuilding,
  resetDemokit,
  site,
}) => {
  const navigate = useNavigate();
  const { permissions } = useStores();
  const canManageOrganization = permissions.has('can_manage_organizations');
  const [state, dispatch] = React.useReducer(getDefaultStateReducer<SiteSettingsFormContainerState>(), {
    name: site?.name || '',
    address: site?.address || '',
    city: site?.city || '',
    postalCode: site?.postalCode || '',
    country: site?.country || '',
    lat: site?.lat || 0,
    long: site?.long || 0,
    alertContactName: site?.alertContactName || '',
    alertContactPhoneNumber: site?.alertContactPhoneNumber || '',
    alertContactEmail: site?.alertContactEmail || '',
    doorCode: site?.doorCode || '',
    vatNumber: site?.vatNumber || '',
    organizationId: site?.organizationId ?? null,
  });
  const [loading, setLoading] = React.useState(false);
  const organizationsQuery = useOrganizations();
  const organizationOptions = React.useMemo(() => {
    const flat = flattenOrganizations(organizationsQuery.data ?? []);
    return flat.map(({ node, depth }) => ({
      value: node.id,
      label: `${'  '.repeat(depth)}${node.name}`,
    }));
  }, [organizationsQuery.data]);
  const handleOrganizationChange = React.useCallback((value: string | null) => {
    dispatch({ value: value ?? null, field: 'organizationId' });
  }, []);
  const handleChange = getDefaultReducerChange<SiteSettingsFormContainerState>(dispatch);
  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      const body: SiteBody = {
        name: state.name,
        address: state.address,
        city: state.city,
        postalCode: state.postalCode,
        country: state.country,
        lat: state.lat,
        long: state.long,
        alertContactName: state.alertContactName,
        alertContactPhoneNumber: state.alertContactPhoneNumber,
        alertContactEmail: state.alertContactEmail,
        doorCode: state.doorCode,
        vatNumber: state.vatNumber,
        organizationId: state.organizationId,
      };
      if (site) {
        if (!canManageOrganization || state.organizationId === (site.organizationId ?? null)) {
          delete body.organizationId;
        }
        site
          .updateBuilding({
            ...body,
          })
          .then(() => {
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      } else {
        createBuilding(state)
          .then((site) => {
            navigate(`/sites/${site.uuid}`, { replace: true });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [state, site, createBuilding, navigate]
  );

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = React.useState(false);
  const [showConfirmResetModal, setShowConfirmResetModal] = React.useState(false);

  const handleDelete = (uuid: string) => {
    return new Promise<void>(() => {
      setLoading(true);
      archiveBuilding(uuid)
        .then(() => {
          setLoading(false);
          setShowConfirmDeleteModal(!showConfirmDeleteModal);
          navigate('/sites');
        })
        .catch((res) => {
          setLoading(false);
          console.error(res);
        });
    });
  };

  const handleReset = (id: number) => {
    return new Promise<void>(() => {
      setLoading(true);
      resetDemokit(id)
        .then(() => {
          setLoading(false);
          setShowConfirmResetModal(!showConfirmResetModal);
          if (site) navigate(`/sites/${site.uuid}`, { replace: true });
        })
        .catch((res) => {
          setLoading(false);
          console.error(res);
        });
    });
  };
  return (
    <>
      <div className="col-md-9">
        <FormPanel>
          <form onSubmit={handleSubmit}>
            <h3>{t('site.form.titles.general')}</h3>
            <Input name="name" onChange={handleChange} value={state.name} required={true} label={t('site.form.name')} />
            {canManageOrganization && (
              <>
                <Select
                  value={state.organizationId}
                  onChange={handleOrganizationChange}
                  label={t('site.form.organization')}
                  placeholder={t('site.form.organizationPlaceholder')}
                  options={organizationOptions}
                  required
                />
                {!state.organizationId && <InputError error={t('site.form.organizationRequired')} />}
              </>
            )}
            <Input
              name="address"
              onChange={handleChange}
              value={state.address}
              required={true}
              placeholder="Street + number"
              label={t('site.form.address')}
            />
            <div className="row">
              <div className="col-md-6">
                <Input
                  name="city"
                  onChange={handleChange}
                  value={state.city}
                  required={true}
                  label={t('site.form.city')}
                />
              </div>
              <div className="col-md-6">
                <Input
                  name="postalCode"
                  onChange={handleChange}
                  value={state.postalCode}
                  required={true}
                  label={t('site.form.zipCode')}
                />
              </div>
            </div>
            <Input
              name="country"
              onChange={handleChange}
              value={state.country}
              required={true}
              label={t('site.form.country')}
            />
            <div className="row">
              <div className="col-md-6">
                <Input
                  name="lat"
                  type="number"
                  onChange={handleChange}
                  value={state.lat}
                  required={true}
                  label={t('site.form.lat')}
                />
              </div>
              <div className="col-md-6">
                <Input
                  name="long"
                  type="number"
                  onChange={handleChange}
                  value={state.long}
                  required={true}
                  label={t('site.form.long')}
                />
              </div>
            </div>

            <h3>{t('site.form.titles.contact')}</h3>
            <div className="row">
              <div className="col-md-6">
                <Input
                  name="alertContactName"
                  type="string"
                  onChange={handleChange}
                  value={state.alertContactName}
                  label={t('site.form.alertContact.name')}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <Input
                  name="alertContactPhoneNumber"
                  type="string"
                  onChange={handleChange}
                  value={state.alertContactPhoneNumber}
                  label={t('site.form.alertContact.phoneNumber')}
                />
              </div>
              <div className="col-md-6">
                <Input
                  name="alertContactEmail"
                  type="email"
                  onChange={handleChange}
                  value={state.alertContactEmail}
                  label={t('site.form.alertContact.email')}
                />
              </div>
            </div>

            <h3>{t('site.form.titles.building')}</h3>
            <div className="row">
              <div className="col-md-6">
                <Input
                  name="doorCode"
                  type="string"
                  onChange={handleChange}
                  value={state.doorCode}
                  label={t('site.form.doorCode')}
                />
              </div>
            </div>

            <h3>{t('site.form.titles.financial')}</h3>
            <div className="row">
              <div className="col-md-6">
                <Input
                  name="vatNumber"
                  type="string"
                  onChange={handleChange}
                  value={state.vatNumber}
                  label={t('site.form.vatNumber')}
                />
              </div>
            </div>

            <SubmitButton disabled={loading || state.name == '' || (canManageOrganization && !state.organizationId)}>
              {site ? t('device.form.update', { type: t('deviceDetail.site') }) : t('site.form.create')}
            </SubmitButton>
          </form>
          {site && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                marginTop: '15px',
                alignItems: 'flex-start',
              }}
            >
              {site.isDemokit && (
                <Button buttonType="error" onClick={() => setShowConfirmResetModal(!showConfirmResetModal)}>
                  {t('siteSettings.resetDemokitModal.reset')}
                </Button>
              )}
              <Button buttonType="error" onClick={() => setShowConfirmDeleteModal(!showConfirmDeleteModal)}>
                {t('siteSettings.deleteSiteModal.delete')}
              </Button>
            </div>
          )}
        </FormPanel>
      </div>
      {showConfirmDeleteModal && site && (
        <DeleteSiteConfirmationModal
          setShowModal={setShowConfirmDeleteModal}
          loading={loading}
          onDelete={handleDelete}
          siteUuid={site.uuid}
        />
      )}
      {showConfirmResetModal && site && (
        <ResetDemokitConfirmationModal
          setShowModal={setShowConfirmResetModal}
          loading={loading}
          onConfirm={handleReset}
          siteId={site.id}
        />
      )}
    </>
  );
};

export default connect((state: State) => ({
  createBuilding: state.sites.createBuilding,
  archiveBuilding: state.sites.archiveBuilding,
  resetDemokit: state.sites.resetDemokit,
}))(SiteSettingsFormContainer);
